import { useMemo } from 'react';
import Icon from '../../ui/Icon/Icon';
import {
  measureContinuousPt,
  measureScenePt,
  formatRuntime,
  PAGE_USABLE_PT,
} from '../../../lib/screenplayEstimate';
import styles from './SceneIndex.module.css';

// Splits the plain-screenplay chapter markdown into scenes anchored by
// `@@SCN <HEADING>` paragraphs. Each scene carries the body lines that follow
// it until the next `@@SCN` (or EOF). Lines before the first heading become a
// nameless "OPENING" entry so the writer still sees their cold open in the
// index when they haven't slugged it yet.
function parseScenes(content) {
  const lines = (content || '').split(/\r?\n/);
  const scenes = [];
  let current = null;
  for (const raw of lines) {
    const m = raw.trim().match(/^@@SCN\s+(.*)$/i);
    if (m) {
      if (current) scenes.push(current);
      current = { title: m[1].trim(), bodyLines: [] };
    } else if (current) {
      current.bodyLines.push(raw);
    } else if (raw.trim()) {
      // Lines outside any scene heading. Group them under a virtual entry so
      // they still count toward the runtime estimate (no click target).
      if (!current) current = { title: '(no heading)', bodyLines: [raw], virtual: true };
    }
  }
  if (current) scenes.push(current);
  return scenes.map((s) => ({
    title: s.title,
    content: s.bodyLines.join('\n'),
    virtual: !!s.virtual,
  }));
}

// Format fractional pages into a short runtime label. <1 page → "<1min" so
// the writer sees something other than "0min" for short slugs.
function formatSceneRuntime(pages) {
  if (pages <= 0) return '0min';
  if (pages < 1) return '<1min';
  return formatRuntime(pages);
}

export default function SceneIndex({ content = '', onJump }) {
  const scenes = useMemo(() => parseScenes(content), [content]);

  // Continuous-flow runtime: measure the whole script as one PDF stream so
  // total pages reflect what `renderScreenplay` will actually paginate, then
  // weight each scene proportionally to its own pt height. Avoids the old
  // "1min per scene" inflation when multiple scenes share a single page.
  const totals = useMemo(() => {
    const totalPt = measureContinuousPt(scenes);
    const totalPages = Math.max(0, totalPt / PAGE_USABLE_PT);
    const perScenePt = scenes.map((s) => measureScenePt(s.content, s.title));
    const perScenePages = perScenePt.map((pt) =>
      totalPt > 0 ? (pt / totalPt) * totalPages : 0,
    );
    return {
      totalPages: Math.max(0, Math.ceil(totalPages)),
      perScenePages,
    };
  }, [scenes]);

  const total = formatRuntime(totals.totalPages);

  // Index into the ProseMirror `.screenplay-scene` DOM list. Virtual scenes
  // (text before the first @@SCN) don't have a corresponding heading node, so
  // we shift the click target by counting only real scenes up to the row.
  const realIndexFor = (i) => {
    let n = -1;
    for (let k = 0; k <= i; k += 1) if (!scenes[k].virtual) n += 1;
    return n;
  };

  return (
    <aside className={styles.sidebar}>
      <header className={styles.header}>
        <span className={styles.heading}>scenes</span>
        <span className={styles.runtime} title="Estimated runtime · 1 page ≈ 1 min">
          {total}
        </span>
      </header>

      {scenes.length === 0 ? (
        <p className={styles.empty}>
          Add a scene heading to start the index.
        </p>
      ) : (
        <ol className={styles.list}>
          {scenes.map((s, i) => {
            const runtime = formatSceneRuntime(totals.perScenePages[i] || 0);
            const number = String(i + 1).padStart(2, '0');
            return (
              <li key={`${i}:${s.title}`} className={styles.row}>
                <button
                  type="button"
                  className={`${styles.rowBtn} ${s.virtual ? styles.rowVirtual : ''}`}
                  onClick={() => {
                    if (s.virtual) return;
                    onJump?.(realIndexFor(i));
                  }}
                  disabled={s.virtual}
                  title={s.virtual ? 'Lines before the first heading' : 'Jump to scene'}
                >
                  <span className={styles.rowNum}>{number}</span>
                  <span className={styles.rowTitle}>
                    {s.virtual ? <em>{s.title}</em> : s.title || <em>(untitled)</em>}
                  </span>
                  <span className={styles.rowRuntime}>{runtime}</span>
                </button>
              </li>
            );
          })}
        </ol>
      )}

      <footer className={styles.footer}>
        <Icon name="Clock" size={11} />
        <span>Estimate updates as you type</span>
      </footer>
    </aside>
  );
}
